import { v3 } from '../math/vec3';

// One place for every tunable. Designers change behaviour here, not by hunting
// magic numbers across systems.

export const SCORING = {
  start: 100,
  hit: 100,
  shot: -10,
  weaponChange: -1,
  /** Score can never drop below this. At `floor` the player cannot shoot. */
  floor: 0,
} as const;

export const ARENA = {
  playerPosition: v3(0, 1, 0),
  /** Enemies spawn at this Z and walk toward the player (+Z). */
  spawnZ: -20,
  /** Enemies spawn at a random X in [-spawnXRange, +spawnXRange]. */
  spawnXRange: 10,
  spawnY: 1,
  enemyScale: 2,
  /** Enemy within this distance of the player ends the game. */
  collisionRadius: 1,
  groundY: -1,
} as const;

export const PROJECTILE = {
  speed: 30,
  ttl: 2.0,
  /** Distance at which a projectile is considered to strike its target. */
  hitRadius: 1,
} as const;

// Difficulty ramp — "the game goes faster and faster". Spawn interval shrinks
// and enemy speed grows as the session goes on; both ease toward a cap over
// `rampSeconds`. Newly spawned enemies sample the current speed, so existing
// enemies keep the pace they were born with.
export const DIFFICULTY = {
  baseSpawnMs: 4000,
  minSpawnMs: 900,
  baseEnemySpeed: 0.6,
  maxEnemySpeed: 3.0,
  rampSeconds: 90,
  secondsPerLevel: 20,
} as const;

// How enemies present themselves.
// - 'digits': the classic look — enemies ARE big extruded glass/metal digits
//   (the legacy aesthetic, revived by user request).
// - 'shapes': enemies are solids encoding the digit (torus=0 …) with a small
//   digit label that fades out per LABELS below.
export const VISUALS = {
  enemyStyle: 'digits' as 'digits' | 'shapes',
  /** Active digit→color palette; see PALETTES in enemyCatalog. */
  palette: 'candy' as string,
} as const;

// Digit labels on enemy shapes ('shapes' style only). Shapes encode the
// numbers; the label is training wheels: fully visible at fadeStartLevel, gone
// by fadeEndLevel, so players progressively learn the shape→number mapping.
export const LABELS = {
  fadeStartLevel: 1,
  fadeEndLevel: 4,
} as const;

// Death-effect tunables (render layer reads these; pure data here).
export const DEATH_FX = {
  /** Base shard kick, world units/sec. */
  impulse: 3,
  gravity: 12,
  restitution: 0.3,
  friction: 0.8,
  /** Seconds a shard lives before it must crumble away. */
  fragmentLife: 1.8,
  /** Seconds of the shrink-into-dust crumble once a shard rests (or expires). */
  crumbleSec: 0.45,
  /** Dust grains emitted when a shard crumbles. */
  dustPerShard: 6,
  /** 0 = pure radial burst, 1 = debris flies entirely along the shot. */
  impactBias: 0.55,
  /** Cheap sphere-sphere impulses between live shards. */
  shardCollisions: true,
} as const;

export function labelOpacity(level: number): number {
  const { fadeStartLevel, fadeEndLevel } = LABELS;
  if (level <= fadeStartLevel) return 1;
  if (level >= fadeEndLevel) return 0;
  return 1 - (level - fadeStartLevel) / (fadeEndLevel - fadeStartLevel);
}

// Combo: consecutive correct answers multiply the hit reward. The classic
// retention loop — streaks are the reason to keep playing.
export const COMBO = {
  tiers: [
    { streak: 3, mult: 2 },
    { streak: 6, mult: 3 },
    { streak: 10, mult: 4 },
  ],
} as const;

export function comboMultiplier(streak: number): number {
  let mult = 1;
  for (const tier of COMBO.tiers) {
    if (streak >= tier.streak) mult = tier.mult;
  }
  return mult;
}

// Power-up stars: spawn chance per enemy spawn, effect durations in seconds.
export const POWERUPS = {
  chance: 0.12,
  driftSpeed: 1.1,
  duration: { timeFreeze: 3, multiShot: 6, hint: 8 },
  /** multiShot fires at up to this many nearest enemies per shot. */
  multiShotTargets: 3,
} as const;

// Hit-feel tunables consumed by app/render layers (subtle by design — the
// audience includes young kids and older adults).
export const JUICE = {
  hitStopSec: 0.07,
  killShakeSec: 0.15,
  killShakeAmp: 0.07,
  slowMoSec: 1.2,
  slowMoScale: 0.35,
} as const;

// Pace presets — the young-kids / older-adults accommodation. Purely
// multiplicative over the one base curve so tuning stays one-dimensional:
// spawnScale stretches the time between enemies, speedScale slows their walk,
// rampScale stretches how long the ramp to maximum takes.
export interface PaceParams {
  spawnScale: number;
  speedScale: number;
  rampScale: number;
}

export const PACE_PRESETS: Readonly<Record<string, PaceParams & { label: string }>> = {
  relaxed: { label: 'Relaxed', spawnScale: 1.6, speedScale: 0.55, rampScale: 2.0 },
  standard: { label: 'Standard', spawnScale: 1, speedScale: 1, rampScale: 1 },
  fast: { label: 'Fast', spawnScale: 0.8, speedScale: 1.25, rampScale: 0.75 },
};

export const DEFAULT_PACE = 'standard';

const clamp01 = (t: number): number => (t < 0 ? 0 : t > 1 ? 1 : t);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const easeInQuad = (t: number): number => t * t;

/** Seconds into the current curriculum stage's ramp. Later stages restart the
 *  ramp part-way up (a breather, not a full reset) so each new exercise eases
 *  in without the game ever feeling like it started over. */
export function stageRampSeconds(elapsedSec: number, stageFromLevel: number): number {
  if (stageFromLevel <= 1) return elapsedSec;
  const stageStart = (stageFromLevel - 1) * DIFFICULTY.secondsPerLevel;
  return Math.max(0, elapsedSec - stageStart) + DIFFICULTY.rampSeconds * 0.35;
}

export function spawnIntervalMs(rampSec: number, pace: PaceParams): number {
  const t = easeInQuad(clamp01(rampSec / (DIFFICULTY.rampSeconds * pace.rampScale)));
  return lerp(DIFFICULTY.baseSpawnMs, DIFFICULTY.minSpawnMs, t) * pace.spawnScale;
}

export function enemySpeed(rampSec: number, pace: PaceParams): number {
  const t = clamp01(rampSec / (DIFFICULTY.rampSeconds * pace.rampScale));
  return lerp(DIFFICULTY.baseEnemySpeed, DIFFICULTY.maxEnemySpeed, t) * pace.speedScale;
}

export function levelFor(elapsedSec: number): number {
  return 1 + Math.floor(elapsedSec / DIFFICULTY.secondsPerLevel);
}
